import React, { useContext, useEffect, useMemo } from 'react';

import { buildMessageEntitySnapshots } from '../../lib/entitySnapshots';
import { useMessageAuthor } from '../../hooks/useMessageAuthor';
import { useRetainedFeedMessage } from '../../hooks/useRetainedFeedMessage';

import CloseXIcon from '../../../assets/images/close-x.svg';

import { EntitySnapshotContext } from '../../contexts/EntitySnapshotContext';
import LocalTime from '../messages/LocalTime';
import MessageAttachments from '../messages/MessageAttachments';
import RichdownContent from '../messages/RichdownContent';
import Flexicon from '../shared/Flexicon';

const SHORT_TIME_THRESHOLD = 60 * 60 * 24; // 24 hours

// Presentational child of <ModalPortal> (mounted by MessageFeed). The portal owns the overlay, focus trap,
// scroll lock, KeyMaster registration and escape handling — this component only renders the message bubble.
// `message` is the renderer row retained by MessageFeed when the modal opened, so virtualization cannot
// blank the modal; the durable feed row is preferred while it exists to keep live edits/reactions current.
const FullMessageModal = ({ message: retainedMessage, onClose }) => {
  const { message, deleted } = useRetainedFeedMessage(retainedMessage?.id, retainedMessage);
  const workspaceId = message?.workspace_id;

  // Sync-originated deletions never dispatch the live-journal destroy event that
  // MessageFeed listens for; close rather than keep showing retained content.
  useEffect(() => {
    if (deleted) onClose?.();
  }, [deleted, onClose]);

  // A missing profile row must not suppress the message body; the retained row's
  // author snapshot (or an Unknown fallback) covers deactivated/uncached authors.
  const author = useMessageAuthor(message, workspaceId, retainedMessage?.author);

  // Get entity snapshots from context and compute them for this message
  const entitySnapshots = useContext(EntitySnapshotContext);

  const messageEntitySnapshots = useMemo(() => {
    if (!message?.text) return {};
    const built = entitySnapshots ? buildMessageEntitySnapshots(message.text, entitySnapshots) : {};
    if (Object.keys(built).length > 0) return built;
    return retainedMessage?.entity_snapshots ?? {};
  }, [message?.text, entitySnapshots, retainedMessage?.entity_snapshots]);

  return (
    <div className="non-interactive-entities flex max-h-[85dvh] w-full flex-col overflow-y-auto px-4 pt-20 pb-4">
      {message && (
        <div className="richdown-bubble--base isolate overflow-visible rounded-3xl p-6">
          {/* Background layer stays behind the content so the avatar overflowing the top is never clipped away. */}
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div aria-hidden className="size-full rounded-3xl border-[0.5px] border-gray-200 bg-gray-200 dark:bg-neutral-800" />
          </div>
          <div className="-mt-20 mb-11 -ml-6 flex items-center justify-start gap-3">
            <Flexicon resource={author} size="size-8" />
            <div className="flex flex-col">
              <p className="text-xs text-gray-100 opacity-50 dark:text-neutral-500 dark:opacity-100">
                <LocalTime
                  timestamp={message.timestamp}
                  format={Date.now() / 1000 - message.timestamp > SHORT_TIME_THRESHOLD ? '%-m/%-d/%y %-I:%M %p' : '%-I:%M %p'}
                />
              </p>
              <p className="text-base leading-none font-semibold text-white">{author?.full_name || 'Unknown'}</p>
            </div>
          </div>
          <div>
            <MessageAttachments
              attachments={message.attachments}
              channelId={message.channel_id}
              messageId={message.id}
              messageSortOrder={message.sort_order}
            />
            {!message.contains_only_attachments && (
              <RichdownContent
                html={message.text}
                richdownVariant="richdown-content--channel-message-not-mine"
                entitySnapshots={messageEntitySnapshots}
                containsOnlyEntities={message.contains_only_entities}
                containsOnlyEmojiEntities={message.contains_only_emoji_entities}
              />
            )}
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={onClose}
        className="fixed top-4 right-6 z-(--stack-modal-close) flex size-11 cursor-pointer items-center justify-center rounded-full bg-neutral-950/80 dark:bg-neutral-800/60"
      >
        <CloseXIcon width={18} height={18} className="cursor-pointer text-white" />
      </button>
    </div>
  );
};

export default FullMessageModal;
